import { useState, useEffect, useCallback } from 'react';
import { stateProvinceService } from '../../api/services/stateProvinceService';
import type {
  StateProvince,
  StateProvinceListItem,
  StateProvinceSearchRequest,
} from '../../api/types/stateProvince.types';
import type { ID } from '../../api/types';

export const useStateProvinces = (params?: StateProvinceSearchRequest) => {
  const [data, setData] = useState<StateProvinceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1, pageSize: 20, totalPages: 0, totalItems: 0,
    hasNextPage: false, hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchItems = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await stateProvinceService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  return { data, loading, error, pagination, refetch: fetchItems };
};

export const useStateProvince = (id: ID | undefined) => {
  const [data, setData] = useState<StateProvince | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchItem = useCallback(async () => {
    if (!id) return;
    setLoading(true); setError(null);
    try {
      const response = await stateProvinceService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchItem(); }, [fetchItem]);

  return { data, loading, error, refetch: fetchItem };
};
